import React, { useEffect, useState } from "react";
import { Input, Button, Form, Alert } from "antd";
import { LockOutlined } from "@ant-design/icons";
import "../../Auth/components/SignIn/styles.css";
import useLogin from "../../../hooks/useLogin";
import API from "../../../API";

/**
 * @class
 * @name ChangePassword
 * @description Change Password form component
 * @version 0.1.0
 * @since 0.1.0
 */
const ChangePassword = () => {
  const [message, setMessage] = useState({ type: '', content: '' });
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, requirements: [] });

  /**
   * @function
   * @name onFinish
   * @description collects values submitted in form
   * and dispatches password change action
   * @param {Object} values
   */
  const onFinish = async (values) => {
    setLoading(true);
    setMessage({ type: '', content: '' });

    try {
      await API.changePassword({
        current_password: values.currentPassword,
        new_password: values.newpassword,
        new_password_confirmation: values.password
      });

      setMessage({ 
        type: 'success', 
        content: 'Password changed successfully!' 
      });
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.errors?.current_password?.[0] ||
                         error.response?.data?.errors?.new_password?.[0] ||
                         'Failed to change password. Please try again.';
      
      setMessage({ type: 'error', content: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (password) => {
    if (password) {
      const validation = API.validatePassword(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation({ isValid: false, requirements: [] });
    }
  };

  const {loading, setLoading} = useLogin(false);

  useEffect(() => {
    // remove active menu item from local storage
    localStorage.removeItem("activeMenuItem");
  }, []);

  return (
    <div className="SignIn">
      <div className="SignInForm">
        <div className="content-body">
          <div>
            <div className="Logo">
              <h5>Change Password</h5>
            </div>
            <div style={{ color: "red" }}>{message.content && message.type === 'error' ? message.content : ''}</div>
            {message.content && (
              <Alert
                message={message.content}
                type={message.type}
                showIcon
                style={{ marginBottom: '16px' }}
                closable
                onClose={() => setMessage({ type: '', content: '' })}
              />
            )}
            <Form autoComplete="off" onFinish={onFinish}>
              {/* cureent password field */}
              <Form.Item
                name="currentPassword"
                rules={[
                  {
                    required: true,
                    message: "Please input your Current Password!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Current Password"
                  data-testid="currentPassword"
                />
              </Form.Item>
              {/* end cureent password field */}
              {/* password field */}
              <Form.Item
                name="newpassword"
                rules={[
                  {
                    required: true,
                    message: "Please input new Password!",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const validation = API.validatePassword(value);
                      return validation.isValid 
                        ? Promise.resolve() 
                        : Promise.reject(new Error('Password does not meet requirements'));
                    }
                  }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="New Password"
                  data-testid="newpassword"
                  onChange={(e) => handlePasswordChange(e.target.value)}
                />
              </Form.Item>
              
              {/* Password Requirements */}
              {passwordValidation.requirements.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Password Requirements:
                  </div>
                  <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
                    {passwordValidation.requirements.map((req) => (
                      <li key={req.key} style={{ 
                        color: req.valid ? '#52c41a' : '#ff4d4f',
                        marginBottom: '2px'
                      }}>
                        {req.valid ? '✓' : '✗'} {req.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* end password field */}
              {/* password field */}
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please input your Password!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Password"
                  data-testid="password"
                />
              </Form.Item>
              {/* end password field */}

              {/* submit button */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="signIn-form-button"
                  loading={loading}
                  disabled={!passwordValidation.isValid}
                >
                  Change Password
                </Button>
              </Form.Item>
              {/* end submit button */}
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
